import { createRoot } from 'react-dom/client';
import type { BoardDoc } from '../../doc/BoardDoc';
import { rectsIntersect, rotatedBounds, unionRects, type Rect } from '../../doc/geometry';
import type { BoardCardItem, FrameItem, Item, ShapeItem, SheetItem } from '../../doc/schema';
import { thumbnailUrl } from '../../app/base';
import { fetchBoardLookup } from '../items/boardSummary';
import { getAssetBlob } from '../assets/localAssets';
import { pickVariant } from '../assets/variants';
import { BoardContext, BoardMetaContext, PaneContext } from '../context';
import { ItemView } from '../items/ItemView';
import { paintStroke } from '../items/StrokeItem';
import { labelLookOf, paintConnector } from '../items/ConnectorItem';
import { effectiveTheme } from '../../theme/theme';
import { createPaneStore } from '../paneStore';
import { rasterStore } from '../paint/rasterStore';
import { elementToImage } from './domSnapshot';

export interface ExportOptions {
  /** Output pixels per world unit. Clamped so the canvas stays within browser limits. */
  scale: number;
  /** CSS colour, or null for transparent. */
  background: string | null;
  padding: number;
}

const MAX_SIDE = 8192;
const MAX_PIXELS = 48_000_000;

/** Items (visible layers only) that intersect the rect, bottom to top. */
export function itemsIn(board: BoardDoc, rect: Rect): Item[] {
  return board.index.query(rect).filter((i) => !board.isHidden(i) && rectsIntersect(rotatedBounds(i), rect));
}

export function boardBounds(board: BoardDoc): Rect | null {
  return unionRects(board.index.all().filter((i) => !board.isHidden(i)).map(rotatedBounds));
}

/**
 * Draws items into a canvas covering `bounds`. Images, shapes and strokes are drawn natively;
 * everything text-based is mounted off-screen and snapshotted through an SVG foreignObject.
 */
export async function rasterize(board: BoardDoc, items: Item[], bounds: Rect, opts: ExportOptions): Promise<HTMLCanvasElement> {
  const area = { x: bounds.x - opts.padding, y: bounds.y - opts.padding, w: bounds.w + opts.padding * 2, h: bounds.h + opts.padding * 2 };
  let scale = opts.scale;
  scale = Math.min(scale, MAX_SIDE / area.w, MAX_SIDE / area.h, Math.sqrt(MAX_PIXELS / (area.w * area.h)));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(area.w * scale));
  canvas.height = Math.max(1, Math.round(area.h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  if (opts.background) {
    ctx.fillStyle = opts.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.scale(scale, scale);
  ctx.translate(-area.x, -area.y);

  const domItems = items.filter((i) => !['image', 'shape', 'stroke', 'connector', 'frame', 'sheet'].includes(i.type));
  const paneEl = document.querySelector(`.pane[data-board="${CSS.escape(board.boardId)}"]`) ?? document.querySelector('.pane');
  const lineColor = (paneEl && getComputedStyle(paneEl).getPropertyValue('--line').trim()) || '#888888';
  // arrow labels keep the box they have on the board, in its canvas colour, also on a transparent export
  const metaBg = board.meta.get('bg');
  const labelLook = labelLookOf(paneEl, opts.background ?? (typeof metaBg === 'string' ? metaBg : null));
  const snapshots = await snapshotDomItems(board, domItems, scale);
  const blend = highlighterBlend();

  for (const item of items) {
    ctx.save();
    ctx.translate(item.x + item.w / 2, item.y + item.h / 2);
    ctx.rotate((item.rotation * Math.PI) / 180);
    ctx.translate(-item.w / 2, -item.h / 2);
    try {
      switch (item.type) {
        case 'image': {
          const px = Math.max(item.w, item.h) * scale;
          const blob = await getAssetBlob(item.props.assetId, pickVariant(px));
          if (blob) {
            const bmp = await createImageBitmap(blob);
            ctx.globalAlpha = item.props.opacity;
            ctx.drawImage(bmp, 0, 0, item.w, item.h);
            bmp.close();
          }
          break;
        }
        case 'shape':
          drawShape(ctx, item);
          break;
        case 'frame':
          drawFrame(ctx, item, lineColor);
          break;
        case 'stroke':
          paintStroke(ctx, item, blend);
          break;
        case 'connector':
          paintConnector(ctx, item, board.index.connectorGeometry(item), labelLook);
          break;
        case 'sheet':
          await drawSheet(ctx, board, item, scale);
          break;
        case 'boardcard': {
          // the cover picture cannot cross into the SVG snapshot: draw it natively underneath
          const snap = snapshots.get(item.id);
          if (snap?.cover) await drawBoardCardCover(ctx, item, snap.cover, scale);
          if (snap) ctx.drawImage(snap.img, 0, 0, item.w, item.h);
          break;
        }
        case 'link': {
          // same for a kept picture, drawn over the image area (opaque in the snapshot)
          const snap = snapshots.get(item.id);
          if (snap) ctx.drawImage(snap.img, 0, 0, item.w, item.h);
          const assetId = item.props.imageAssetId;
          if (snap?.cover && assetId) {
            const blob = await getAssetBlob(assetId, pickVariant(Math.max(snap.cover.w, snap.cover.h) * scale));
            if (blob) await drawFitted(ctx, blob, snap.cover, 'cover');
          }
          break;
        }
        default: {
          const snap = snapshots.get(item.id);
          if (snap) ctx.drawImage(snap.img, 0, 0, item.w, item.h);
        }
      }
    } catch (e) {
      console.warn('[export] skipped item', item.id, e);
    }
    ctx.restore();
  }
  return canvas;
}

interface DomSnapshot {
  img: HTMLImageElement;
  /** Board cards: where the cover picture sits inside the item (item units). */
  cover: Rect | null;
}

async function snapshotDomItems(board: BoardDoc, items: Item[], scale: number): Promise<Map<string, DomSnapshot>> {
  const out = new Map<string, DomSnapshot>();
  if (!items.length) return out;
  // Mount the bodies off-screen with the real components so CSS and fonts match the board.
  const host = document.createElement('div');
  host.className = 'export-host';
  host.style.cssText = 'position:fixed;left:-100000px;top:0;width:1px;height:1px;overflow:visible;';
  const pane = document.querySelector('.pane');
  if (pane) {
    host.setAttribute('data-look', pane.getAttribute('data-look') ?? '');
    host.setAttribute('style', host.style.cssText + (pane.getAttribute('style') ?? ''));
    host.classList.add('pane');
  }
  document.body.appendChild(host);
  const root = createRoot(host);
  const paneStore = createPaneStore();
  await new Promise<void>((resolve) => {
    root.render(
      <BoardContext.Provider value={board}>
        <BoardMetaContext.Provider value={{ title: '', role: 'viewer' }}>
          <PaneContext.Provider value={paneStore}>
            <div className="world" style={{ transform: 'none' }} ref={() => resolve()}>
              {items.map((i) => (
                <ItemView key={i.id} id={i.id} />
              ))}
            </div>
          </PaneContext.Provider>
        </BoardMetaContext.Provider>
      </BoardContext.Provider>,
    );
  });
  await document.fonts.ready;
  await new Promise((r) => setTimeout(r, 50)); // editors mount their content a tick later
  try {
    for (const item of items) {
      const el = host.querySelector(`[data-item-id="${item.id}"]`);
      if (!el) continue;
      try {
        const coverEl = el.querySelector<HTMLElement>('[data-cover]');
        const cover = coverEl ? { x: coverEl.offsetLeft, y: coverEl.offsetTop, w: coverEl.offsetWidth, h: coverEl.offsetHeight } : null;
        out.set(item.id, { img: await elementToImage(el, item.w, item.h, scale), cover });
      } catch (e) {
        console.warn('[export] snapshot failed', item.id, e);
      }
    }
  } finally {
    root.unmount();
    host.remove();
  }
  return out;
}

/** The picture behind a board card's cover area: its custom cover asset, else the target board's preview. */
async function coverBlob(item: BoardCardItem, px: number): Promise<Blob | undefined> {
  const { coverAssetId, showPreview, boardId } = item.props;
  if (coverAssetId) return getAssetBlob(coverAssetId, pickVariant(px));
  if (!showPreview) return undefined;
  const lookup = await fetchBoardLookup(boardId);
  if (!lookup.board?.thumbnailAt) return undefined;
  try {
    const res = await fetch(thumbnailUrl(boardId, lookup.board.thumbnailAt), { credentials: 'same-origin' });
    return res.ok ? await res.blob() : undefined;
  } catch {
    return undefined;
  }
}

async function drawBoardCardCover(ctx: CanvasRenderingContext2D, item: BoardCardItem, cover: Rect, scale: number): Promise<void> {
  const blob = await coverBlob(item, Math.max(cover.w, cover.h) * scale);
  if (blob) await drawFitted(ctx, blob, cover, item.props.fit);
}

/** Draws a picture centred in `rect`, scaled to cover it or to fit inside, clipped to the rect. */
async function drawFitted(ctx: CanvasRenderingContext2D, blob: Blob, rect: Rect, fit: 'cover' | 'contain'): Promise<void> {
  const bmp = await createImageBitmap(blob);
  try {
    const ratio = Math[fit === 'contain' ? 'min' : 'max'](rect.w / bmp.width, rect.h / bmp.height);
    const w = bmp.width * ratio;
    const h = bmp.height * ratio;
    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.clip();
    ctx.drawImage(bmp, rect.x + (rect.w - w) / 2, rect.y + (rect.h - h) / 2, w, h);
    ctx.restore();
  } finally {
    bmp.close();
  }
}

/** How highlighter ink mixes with what is under it, as on the board (see board.css). */
export function highlighterBlend(): GlobalCompositeOperation {
  return effectiveTheme() === 'dark' ? 'screen' : 'multiply';
}

/** Sketch sheet: the paper, then each visible layer (its live canvas while painting, else its asset). */
async function drawSheet(ctx: CanvasRenderingContext2D, board: BoardDoc, item: SheetItem, scale: number): Promise<void> {
  const { paper, layers, pxW, pxH } = item.props;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, item.w, item.h);
  ctx.clip();
  if (paper) {
    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, item.w, item.h);
  }
  const store = rasterStore(board);
  const px = Math.max(item.w, (item.w * pxH) / Math.max(1, pxW)) * scale;
  const ordered = Object.entries(layers).sort((a, b) => a[1].order - b[1].order);
  for (const [id, l] of ordered) {
    if (!l.visible) continue;
    ctx.globalAlpha = l.opacity;
    const live = store.liveLayer(item.id, id);
    if (live?.loaded) {
      ctx.drawImage(live.canvas, 0, 0, item.w, item.h);
      continue;
    }
    if (!l.assetId) continue;
    const blob = await getAssetBlob(l.assetId, pickVariant(px));
    if (!blob) continue;
    const bmp = await createImageBitmap(blob);
    ctx.drawImage(bmp, 0, 0, item.w, item.h);
    bmp.close();
  }
  ctx.restore();
}

/** Frame: tinted box, 2px border and the label sitting on top of the top-left corner. */
export function drawFrame(ctx: CanvasRenderingContext2D, item: FrameItem, lineColor: string): void {
  const color = item.props.color ?? lineColor;
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.06;
  ctx.fillRect(0, 0, item.w, item.h);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, item.w - 2, item.h - 2);
  const label = (item.props.title || 'Frame').toUpperCase();
  ctx.font = '700 12px ui-monospace, Menlo, Consolas, monospace';
  const tw = Math.min(item.w, ctx.measureText(label).width + 16);
  ctx.fillRect(-2, -22, tw, 18);
  ctx.save();
  ctx.beginPath();
  ctx.rect(-2, -22, tw, 18);
  ctx.clip();
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, 6, -13);
  ctx.restore();
}

export function drawShape(ctx: CanvasRenderingContext2D, item: ShapeItem): void {
  const { kind, fill, stroke, strokeWidth: sw, flip } = item.props;
  const { w, h } = item;
  ctx.lineWidth = sw;
  ctx.strokeStyle = stroke === 'none' ? 'transparent' : stroke;
  ctx.fillStyle = fill === 'none' ? 'transparent' : fill;
  ctx.lineCap = 'square';
  if (kind === 'rect') {
    if (fill !== 'none') ctx.fillRect(sw / 2, sw / 2, Math.max(0, w - sw), Math.max(0, h - sw));
    if (stroke !== 'none' && sw > 0) ctx.strokeRect(sw / 2, sw / 2, Math.max(0, w - sw), Math.max(0, h - sw));
    return;
  }
  if (kind === 'ellipse') {
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, Math.max(0, (w - sw) / 2), Math.max(0, (h - sw) / 2), 0, 0, Math.PI * 2);
    if (fill !== 'none') ctx.fill();
    if (stroke !== 'none' && sw > 0) ctx.stroke();
    return;
  }
  const x1 = 0;
  const y1 = flip ? h : 0;
  const x2 = w;
  const y2 = flip ? 0 : h;
  let ex = x2;
  let ey = y2;
  if (kind === 'arrow') {
    const size = Math.min(Math.max(10, sw * 4), Math.hypot(x2 - x1, y2 - y1));
    const angle = Math.atan2(y2 - y1, x2 - x1);
    ex = x2 - Math.cos(angle) * size;
    ey = y2 - Math.sin(angle) * size;
    const half = size * 0.45;
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(ex + Math.cos(angle + Math.PI / 2) * half, ey + Math.sin(angle + Math.PI / 2) * half);
    ctx.lineTo(ex + Math.cos(angle - Math.PI / 2) * half, ey + Math.sin(angle - Math.PI / 2) * half);
    ctx.closePath();
    ctx.fillStyle = stroke;
    ctx.fill();
  }
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(ex, ey);
  ctx.stroke();
}

export function downloadCanvas(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('PNG encoding failed'));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      resolve();
    }, 'image/png');
  });
}
